/*
 * Selo search suggest.
 * --------------------
 * `SeloSuggest.Combobox` is the shared ARIA-combobox core behind both
 * predictive dropdowns in the theme:
 *
 *   <site-search-suggest>  the header's sitewide autocomplete — Products,
 *                          Categories and Knowledge Hub, defined here.
 *   <kb-search-suggest>    the Knowledge Hub hero's article-scoped dropdown,
 *                          defined in `selo-kb-widgets.js`, which subclasses
 *                          this file rather than carrying its own copy.
 *
 * Both are progressive enhancement over a plain GET form: without this file,
 * with `predictive_search_enabled` off, or on any request failure, submitting
 * the form is the whole feature.
 *
 * Load order matters — `selo-kb-widgets.js` reads `window.SeloSuggest` at parse
 * time, so every page that loads it also loads this first (the header does so
 * sitewide, and the hub loads it explicitly so the pair is not hostage to the
 * header's presence). The IIFE is idempotent for that double include.
 *
 * openspec change: redesign-header-footer. Spec: global-search-suggest.
 */
(function () {
  'use strict';

  if (window.SeloSuggest) return;

  var DEBOUNCE = 250;
  var MIN_QUERY = 2;
  var GROUP_LIMIT = 4;
  /* Must match the panel's `transition-duration` in components/suggest.css and
     the `.s-kb-suggest__panel` rule in selo-kb.css. */
  var EXIT = 140;

  /* Trailing-edge debounce; the returned function exposes `.cancel()`. */
  function debounce(fn, wait) {
    var timer = null;
    function debounced() {
      var args = arguments;
      var self = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn.apply(self, args);
      }, wait);
    }
    debounced.cancel = function () {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    return debounced;
  }

  /*
   * Single-flight fetch: starting a new request aborts the one in flight, so a
   * slow earlier response can never overwrite a newer one. Aborts surface as a
   * resolved `null` rather than a rejection, since a superseded request is not
   * an error the caller needs to handle.
   */
  function singleFlight() {
    var controller = null;
    return function (url) {
      if (controller) controller.abort();
      controller = new AbortController();
      var mine = controller;
      return fetch(url, { signal: mine.signal }).then(
        function (response) {
          if (mine !== controller) return null;
          if (!response.ok) throw new Error('Request failed: ' + response.status);
          return response;
        },
        function (error) {
          if (error && error.name === 'AbortError') return null;
          throw error;
        }
      );
    };
  }

  function define(name, ctor) {
    if (!window.customElements || window.customElements.get(name)) return;
    window.customElements.define(name, ctor);
  }

  /* --------------------------------------------------------------------- *
   * Combobox core
   * --------------------------------------------------------------------- */

  /*
   * Subclasses supply four things and inherit everything else:
   *
   *   inputSelector / panelSelector  where to find the two elements
   *   optionAttr                     the data attribute marking an option
   *   buildUrl(query)                the predictive-search request
   *   buildGroups(doc, query)        ordered group nodes for the panel
   */
  class Combobox extends HTMLElement {
    get inputSelector() {
      return '[data-suggest-input]';
    }

    get panelSelector() {
      return '.s-suggest__panel';
    }

    get optionAttr() {
      return 'data-suggest-option';
    }

    get minQuery() {
      return MIN_QUERY;
    }

    /* Read by the sticky header: revealing the bar would tear the panel off
       its input mid-scroll. */
    get isOpen() {
      return !!this.panel && !this.panel.hidden;
    }

    connectedCallback() {
      if (this.dataset.suggestReady === 'true') return;
      if (this.dataset.suggestEnabled !== 'true') return;

      this.input = this.querySelector(this.inputSelector);
      this.panel = this.querySelector(this.panelSelector);
      if (!this.input || !this.panel) return;
      this.dataset.suggestReady = 'true';

      this.options = [];
      this.activeIndex = -1;
      this.exitTimer = null;
      this.request = singleFlight();
      this.onInput = debounce(this.search.bind(this), DEBOUNCE);

      /*
       * Combobox wiring lands here rather than in Liquid: until this point the
       * input has no popup, and announcing one that cannot open is worse than
       * announcing nothing.
       */
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.panel.id);
      this.panel.setAttribute('role', 'listbox');

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.panel.addEventListener('mousedown', this.onPanelMouseDown.bind(this));

      /*
       * Coming back to a field that still holds a query should show its results
       * again — `close()` throws the markup away, so this refetches rather than
       * unhiding. `click` covers the case where the input never lost focus:
       * Escape closed the panel and the visitor clicks the field to reopen it.
       */
      var onReopen = this.onReopen.bind(this);
      this.input.addEventListener('focus', onReopen);
      this.input.addEventListener('click', onReopen);

      this.ready();
    }

    /* Hook for subclass setup that needs the element wired up first. */
    ready() {}

    /* ----------------------------------------------------------------- */

    buildUrl() {
      throw new Error('buildUrl must be implemented by the subclass');
    }

    buildGroups() {
      throw new Error('buildGroups must be implemented by the subclass');
    }

    search() {
      var query = this.input.value.trim();
      if (query.length < this.minQuery) {
        this.close();
        return;
      }

      var self = this;
      this.request(this.buildUrl(query))
        .then(function (response) {
          /* null = superseded by a newer query; leave the dropdown alone. */
          if (!response) return null;
          return response.text();
        })
        .then(function (markup) {
          if (markup === null || markup === undefined) return;
          self.render(markup, query);
        })
        .catch(function () {
          /* A failed suggestion is not worth an error state — the form works. */
          self.close();
        });
    }

    render(markup, query) {
      /* A close is possibly still fading out; its timer would empty the panel
         we are about to fill. */
      this.cancelExit();

      var fetched = new DOMParser().parseFromString(markup, 'text/html');
      var nodes = this.buildGroups(fetched, query) || [];

      this.panel.innerHTML = '';
      nodes.forEach(function (node) {
        if (node) this.panel.appendChild(node);
      }, this);

      this.options = Array.prototype.slice.call(
        this.panel.querySelectorAll('[' + this.optionAttr + ']')
      );
      if (!this.options.length) {
        this.close();
        return;
      }

      this.setActive(-1);
      this.panel.hidden = false;
      /*
       * One frame between "laid out" and "visible": `[hidden]` is
       * `display: none`, and a transition cannot start from a box that does not
       * exist yet. Skipped when the panel is already open — a fresh set of
       * results replaces the old ones without blinking.
       */
      if (this.panel.classList.contains('is-open')) {
        this.input.setAttribute('aria-expanded', 'true');
        return;
      }
      var self = this;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          self.panel.classList.add('is-open');
        });
      });
      this.input.setAttribute('aria-expanded', 'true');
    }

    cancelExit() {
      if (!this.exitTimer) return;
      clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }

    /* ----------------------------------------------------------------- */

    setActive(index) {
      this.activeIndex = index;
      this.options.forEach(function (option, position) {
        var isActive = position === index;
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
        option.classList.toggle('is-active', isActive);
      });

      if (index < 0) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      var active = this.options[index];
      this.input.setAttribute('aria-activedescendant', active.id);
      if (active.scrollIntoView) active.scrollIntoView({ block: 'nearest' });
    }

    move(step) {
      if (!this.options.length) return;
      var next = this.activeIndex + step;
      /* Wrap through the whole listbox, groups included. */
      if (next < 0) next = this.options.length - 1;
      if (next > this.options.length - 1) next = 0;
      this.setActive(next);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (this.panel.hidden) return;
        event.preventDefault();
        this.move(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (event.key === 'Enter') {
        /* No active option: let the form submit the plain search. */
        if (this.panel.hidden || this.activeIndex < 0) return;
        event.preventDefault();
        window.location.href = this.options[this.activeIndex].dataset.url;
      }
    }

    /*
     * `mousedown` fires before the input loses focus, so acting here keeps a
     * click on a suggestion from being cancelled by the close-on-blur below.
     */
    onPanelMouseDown(event) {
      var option = event.target.closest('[' + this.optionAttr + ']');
      if (!option) return;
      event.preventDefault();
      window.location.href = option.dataset.url;
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.close();
    }

    onReopen() {
      /* `.is-open`, not `hidden`: during the exit fade the panel is still laid
         out, and coming back to the field then should reopen it. */
      if (this.panel.classList.contains('is-open')) return;
      if (this.input.value.trim().length < this.minQuery) return;
      this.search();
    }

    /*
     * The state goes first and the markup follows once the fade has run: the
     * combobox reads as closed to assistive tech and to `onReopen` immediately,
     * while the panel is still on screen on its way out. `isOpen` deliberately
     * stays true for that window — the sticky header must not reveal itself
     * underneath a panel that is still visible.
     */
    close() {
      this.onInput.cancel();
      this.options = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.panel.classList.remove('is-open');

      if (this.panel.hidden) {
        this.cancelExit();
        this.panel.innerHTML = '';
        return;
      }

      var self = this;
      this.cancelExit();
      this.exitTimer = setTimeout(function () {
        self.exitTimer = null;
        self.panel.hidden = true;
        self.panel.innerHTML = '';
      }, EXIT);
    }
  }

  /* --------------------------------------------------------------------- *
   * Sitewide suggest
   * --------------------------------------------------------------------- */

  /*
   * Header autocomplete: Products, Categories (collections) and Knowledge Hub
   * articles, four each, rendered by `sections/site-search-suggest.liquid`.
   *
   * `resources[options][fields]` is what makes a part number work: without it
   * the predictive endpoint matches titles and tags only, so an exact variant
   * SKU returns nothing. `body` is deliberately left out — it drags in prose
   * matches that bury the part the visitor typed.
   */
  class SiteSearchSuggest extends Combobox {
    buildUrl(query) {
      return (
        this.dataset.predictiveUrl +
        '?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,collection,article' +
        '&resources[limit]=' +
        GROUP_LIMIT +
        '&resources[limit_scope]=each' +
        '&resources[options][fields]=title,product_type,variants.title,variants.sku' +
        '&section_id=site-search-suggest'
      );
    }

    /*
     * The section already emits the groups in design order and omits the empty
     * ones, so there is nothing to reorder here — unlike the KB dropdown,
     * whose Categories come from a client-side index.
     */
    buildGroups(doc) {
      var wrapper = doc.querySelector('[data-suggest-groups]');
      if (!wrapper) return [];
      return Array.prototype.slice.call(wrapper.children);
    }
  }

  window.SeloSuggest = {
    Combobox: Combobox,
    debounce: debounce,
    singleFlight: singleFlight,
    define: define,
    GROUP_LIMIT: GROUP_LIMIT,
    MIN_QUERY: MIN_QUERY,
  };

  define('site-search-suggest', SiteSearchSuggest);
})();
